<script lang="ts" setup>
import type { ContentConversation } from '~/models/Content/ContentConversation'
import { computed, provide, useTemplateRef, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import { ContentType } from '~/models/Content/ContentType'
import { ColorTheme } from '~/models/Content/Color'
import { useOverlayOutlet } from '~/composables/useOverlayOutlet'
import { useColorTheme } from '~/composables/useColorTheme'
import { useContentEvent } from '~/composables/activity/useContentEvent'
import useContentApi from '~/api/contentApi'
import TopBar from '~/components/utils/TopBar.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import ConversationList from '~/components/conversation/ConversationList.vue'
import ArticleNavigation from '~/components/article/ArticleNavigation.vue'

const props = defineProps<{ locationId: number }>()

const { findContents } = useContentApi()
const { checkItemLicense } = useLicenseControlStore()

const { theme, style } = useColorTheme(ColorTheme.BlueOrange)
provide('theme', theme)

const overlayOutlet = useOverlayOutlet(useTemplateRef('overlay-outlet'))

const { data: conversation, isLoading } = useQuery({
  enabled: props.locationId > 0,
  queryKey: computed(() => ['conversation-view', props.locationId]),
  queryFn: () => findContents<ContentConversation>({
    locationIdCriterion: [props.locationId],
    contentTypeCriterion: [ContentType.Conversation],
  }, 1).then(([data]) => data),
  staleTime: Infinity,
  retry: 1,
})

const { autoContentEvent } = useContentEvent()
autoContentEvent(conversation)

watch(conversation, () => {
  if (!conversation.value) {
    return
  }
  setTitle(conversation.value.title)
  checkItemLicense(conversation.value)
}, { immediate: true })
</script>

<template>
  <div :style class="flex h-full flex-col bg-(--theme-5) text-[1.2rem]">
    <NotFound v-if="!isLoading && !conversation" />
    <template v-else-if="conversation">
      <header>
        <TopBar
          :item="conversation"
          :fullscreen="false"
        />
        <h1
          class="sr-only"
          v-text="conversation.title"
        />
      </header>
      <div class="mb-auto pb-20">
        <div class="au-prose mx-auto h-full max-w-screen-md p-6">
          <ConversationList :item="conversation" />
        </div>
      </div>
      <div
        :id="overlayOutlet.id"
        ref="overlay-outlet"
        class="sticky bottom-0 z-100 flex-none bg-(--theme-60) text-(--theme-10) empty:hidden [&:not(:empty)]:p-4"
        :style="{
          ...style,
          '--media-color': 'var(--theme-10)',
          '--media-bg-color': 'var(--theme-60)',
        }"
      />
      <ArticleNavigation
        class="h-max flex-none grow-0"
        :item="conversation"
      />
    </template>
  </div>
</template>
